<script lang="ts">
  /// メンバー招待モーダル (ACC-7 / #418)。メンバー画面と組織詳細画面で共有する。
  ///
  /// FSD: features はユースケース UI + フロー判断を持つ。複数 Auth0 ロール選択 (RoleMultiSelect)
  /// と inviteAccount の呼び出しをここに閉じ込め、ページは「org / client を渡して結果を受け取る」
  /// だけにする。ロールカタログは org スコープ read (listOrgRoles) で取得し、org_admin も
  /// 自組織分を読める。付与上限と org スコープ強制の真実は backend。
  import { onMount } from 'svelte';
  import { inviteAccount } from '@shared/api/accounts';
  import { listOrgRoles } from '@shared/api/admin';
  import type { AdminRole, Auth0Client } from '@shared/types/admin';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import RoleMultiSelect from '@shared/ui/RoleMultiSelect.svelte';

  interface Props {
    /// 招待先 Auth0 organization。未解決なら送信させない。
    orgId: string | null;
    /// 既定の招待元 SPA client_id。`clients` 未指定ならこれを固定で使う。
    clientId: string | null;
    /// client の選択候補 (組織詳細では SPA 一覧を渡す)。未指定なら clientId 固定 (メンバー画面)。
    clients?: Auth0Client[];
    onClose: () => void;
    /// 招待成功時。親は一覧を refresh する。
    onInvited: () => void | Promise<void>;
  }

  const { orgId, clientId, clients, onClose, onInvited }: Props = $props();

  const actorRole = $derived(sessionStore.user?.role ?? null);

  let email = $state('');
  let displayName = $state('');
  let roleIds = $state<string[]>([]);
  let selectedClientId = $state(clientId ?? '');

  let roles = $state<AdminRole[]>([]);
  let rolesError = $state<string | null>(null);
  let ready = $state(false);

  let submitting = $state(false);
  let formError = $state<string | null>(null);

  onMount(async () => {
    if (!orgId) {
      rolesError = '招待先の組織が解決できていません。';
      ready = true;
      return;
    }
    try {
      roles = await listOrgRoles(orgId);
    } catch (err) {
      rolesError = err instanceof Error ? err.message : String(err);
    } finally {
      ready = true;
    }
  });

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    const client = (clients ? selectedClientId : clientId)?.trim();
    if (!orgId || !client) {
      formError = '招待先の組織 / client が解決できていません。';
      return;
    }
    if (roleIds.length === 0) {
      formError = '権限ロールを最低1つ選んでください。';
      return;
    }
    submitting = true;
    formError = null;
    try {
      await inviteAccount({
        email: email.trim(),
        display_name: displayName.trim() || undefined,
        role_ids: roleIds,
        organization_id: orgId,
        client_id: client,
      });
      await onInvited();
      onClose();
    } catch (err) {
      formError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }

  function handleClose() {
    if (submitting) return;
    onClose();
  }
</script>

<div class="modal-backdrop">
  <button type="button" class="modal-dismiss" aria-label="閉じる" onclick={handleClose}></button>
  <div class="modal-card" role="dialog" aria-modal="true">
    <h2>メンバーを招待</h2>
    <form onsubmit={handleSubmit}>
      <label>
        <span>メールアドレス <em>*</em></span>
        <input bind:value={email} type="email" placeholder="user@example.com" required />
      </label>
      <label>
        <span>表示名</span>
        <input bind:value={displayName} placeholder="(未入力なら初回ログイン時に補完)" />
      </label>
      {#if clients}
        <label>
          <span>client <em>*</em></span>
          <select bind:value={selectedClientId} required>
            {#each clients as c (c.client_id)}
              <option value={c.client_id}>{c.name}</option>
            {/each}
          </select>
        </label>
      {/if}
      <div class="field">
        <span class="field-label">ロール <em>*</em></span>
        {#if !ready}
          <small class="hint">ロールを読込中…</small>
        {:else if rolesError}
          <small class="error">ロールの取得に失敗しました: {rolesError}</small>
        {:else}
          <RoleMultiSelect bind:value={roleIds} {roles} {actorRole} />
          <small class="hint">権限ロール (スーパー管理者 / 組織管理者 / メンバー / 閲覧のみ) を最低1つ選んでください。</small>
        {/if}
      </div>
      {#if formError}
        <div class="error">{formError}</div>
      {/if}
      <div class="modal-actions">
        <button type="button" class="btn" disabled={submitting} onclick={handleClose}>
          キャンセル
        </button>
        <button type="submit" class="btn btn-primary" disabled={submitting}>
          {submitting ? '送信中…' : '招待を送信'}
        </button>
      </div>
    </form>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    z-index: 100;
  }
  .modal-dismiss {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    border: 0;
    padding: 0;
    cursor: pointer;
  }
  .modal-card {
    position: relative;
    background: var(--bg, #fff);
    padding: 20px;
    border-radius: 8px;
    min-width: 360px;
    max-width: 90vw;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  .modal-card h2 {
    margin: 0 0 12px;
  }
  .modal-card form label,
  .field {
    display: block;
    margin-bottom: 12px;
  }
  .modal-card form label span,
  .field-label {
    display: block;
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 4px;
  }
  .modal-card form input,
  .modal-card form select {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid var(--border);
    border-radius: 4px;
  }
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
  .hint {
    color: var(--muted);
    font-size: 11px;
  }
  .error {
    color: var(--danger, #c83232);
    background: rgba(200, 50, 50, 0.06);
    padding: 8px;
    border-radius: 4px;
    margin: 8px 0;
  }
</style>
